"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface FieldErrors {
  email?: string[];
  password?: string[];
  slug?: string[];
  general?: string[];
}

interface RegisterFormProps {
  title?: string;
  description?: string;
  submitLabel?: string;
}

export function RegisterForm({
  title = "Registrieren",
  description = "Erstelle dein Konto und werde Teil der MicMatch-Community.",
  submitLabel = "Registrieren",
}: RegisterFormProps = {}) {
  const router = useRouter();
  const [errors, setErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    setGeneralError(null);
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const slug = formData.get("slug") as string;

    try {
      // Register
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, slug }),
      });

      if (!res.ok) {
        const data = await res.json();
        if (data.error && typeof data.error === "object") {
          setErrors(data.error);
        } else {
          setGeneralError("Registrierung fehlgeschlagen.");
        }
        setLoading(false);
        return;
      }

      // Auto-login after successful registration
      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        setGeneralError("Konto erstellt, aber Anmeldung fehlgeschlagen. Bitte manuell anmelden.");
        router.push("/login");
      } else {
        router.push("/onboarding");
        router.refresh();
      }
    } catch {
      setGeneralError("Ein Fehler ist aufgetreten. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <form onSubmit={onSubmit}>
        <CardContent className="space-y-4">
          {(generalError || errors.general?.[0]) && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {generalError || errors.general?.[0]}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="deine@email.de"
              required
              autoComplete="email"
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email[0]}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="slug">Benutzername (URL)</Label>
            <Input
              id="slug"
              name="slug"
              type="text"
              placeholder="mein-name"
              required
              autoComplete="username"
              pattern="[a-z0-9-]+"
              minLength={3}
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              Dein Profil wird erreichbar unter: micmatch.de/g/<span className="font-mono">dein-name</span>
            </p>
            {errors.slug && (
              <p className="text-sm text-destructive">{errors.slug[0]}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Passwort</Label>
            <Input
              id="password"
              name="password"
              type="password"
              required
              autoComplete="new-password"
              minLength={8}
            />
            <p className="text-xs text-muted-foreground">Mindestens 8 Zeichen</p>
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password[0]}</p>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Wird erstellt..." : submitLabel}
          </Button>
          <p className="text-sm text-muted-foreground">
            Bereits ein Konto?{" "}
            <Link href="/login" className="text-primary hover:underline">
              Anmelden
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}
